/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction, when } from 'mobx';
import { getCluProjectService } from 'src/clu/core/services/projects/cluProjectService';
import { VerificationSwagger } from 'src/clu/core/services/trainedModels/cluModelVerificationSwagger';
import { getCluTrainedModelService } from 'src/clu/core/services/trainedModels/cluTrainedModelService';
import { CluTrainedModelEvaluationSwagger } from 'src/clu/core/services/trainedModels/cluTrainedModelSwaggers';
import {
    cluModelGuidanceSwaggersToStore,
    cluTrainedModelEvaluationSwaggerToModelEvaluationStores,
    cluTrainedModelSwaggerToCluTrainedModelStore
} from 'src/clu/modules/handlers/trainedModels/cluTrainedModelMappers';
import { RootStore } from 'src/core/stores/RootStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { batchPromises, getNextAbleResults } from 'src/core/utils/batchPromises';
import { EvaluationJobStore } from 'src/modules/modelEvaluation/EvaluationJobInfoStore';
import { getEvaluationJobOperationHandler } from 'src/modules/modelEvaluation/utils/evaluationUtils';
import { TrainedModelSwagger } from 'src/modules/modelTraining/types/trainingSwaggers';

export type CluTrainedModelHandlerGroup = ReturnType<typeof createCluTrainedModelHandlerGroup>;

export const loadCluTrainedModelsHelper = async (rootStore: RootStore, forceRefresh: boolean = false) => {
    const currentProject = rootStore.currentCluProjectStore;
    const { tracker } = currentProject.trainedModelStoreHouse;

    if (!forceRefresh && (tracker.isRunning || tracker.isCompleted)) {
        return;
    }

    try {
        tracker.setState('running');

        const trainedModelsData = await getNextAbleResults<'value', TrainedModelSwagger>(
            (nextLink: string) =>
                getCluTrainedModelService().getCluTrainedModels({
                    nextLink,
                    projectName: currentProject.name
                }),
            'value'
        );
        const trainedModelStores = trainedModelsData.map(cluTrainedModelSwaggerToCluTrainedModelStore);

        transaction(() => {
            currentProject.trainedModelStoreHouse.setItems(trainedModelStores);
            tracker.setState('completed');
        });
    } catch (e) {
        tracker.setState('failed', <Error>e);
        throw e;
    }
};

export const createCluTrainedModelHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore, lifetime } = dependencies;

    const getCluTrainedModels = async ({}: HandlerParams<object>) => loadCluTrainedModelsHelper(rootStore);

    const getCluTrainedModelEvaluationData = async ({
        trainedModelNames,
        isRefreshEvaluation = false
    }: HandlerParams<{ trainedModelNames: string[]; isRefreshEvaluation?: boolean }>) => {
        const currentProject = rootStore.currentCluProjectStore;
        const trainedModelsStoreHouse = currentProject.trainedModelStoreHouse;

        if (trainedModelsStoreHouse.tracker.isRunning) {
            await when(() => trainedModelsStoreHouse.tracker.isCompleted);
        } else if (!trainedModelsStoreHouse.tracker.isCompleted) {
            await loadCluTrainedModelsHelper(rootStore);
        }

        const modelService = getCluTrainedModelService();
        const modelsToFetch = currentProject.trainedModelStoreHouse.items
            .filter(model => trainedModelNames.includes(model.name))
            .filter(model => (!model.evaluationTracker.isCompleted || isRefreshEvaluation) && !model.evaluationTracker.isRunning);

        transaction(() => modelsToFetch.forEach(m => m.evaluationTracker.setState('running')));

        const promiseResults = await batchPromises(
            modelsToFetch.map(model =>
                modelService.getCluTrainedModelEvaluationData({ projectName: currentProject.name, trainedModelName: model.name })
            )
        );

        transaction(() =>
            promiseResults.results.forEach((result, index) => {
                if (result.status === 'success') {
                    const stores = cluTrainedModelEvaluationSwaggerToModelEvaluationStores(
                        result.success as CluTrainedModelEvaluationSwagger
                    );

                    modelsToFetch[index].setIntentEvaluationStore(stores.intentsEvaluation);
                    modelsToFetch[index].setEntityEvaluationStore(stores.entitiesEvaluation);
                    modelsToFetch[index].evaluationTracker.setState('completed');
                } else {
                    modelsToFetch[index].setIntentEvaluationStore(null);
                    modelsToFetch[index].setEntityEvaluationStore(null);
                    modelsToFetch[index].evaluationTracker.setState('failed', <Error>result.error);
                }
            })
        );
    };

    const getCluTrainedModelVerificationData = async ({ trainedModelName }: HandlerParams<{ trainedModelName: string }>) => {
        const { name: projectName } = rootStore.currentCluProjectStore;

        const currentProject = rootStore.currentCluProjectStore;
        const currentModel = currentProject.trainedModelStoreHouse.items.find(m => m.name === trainedModelName);

        const modelVerificationData = await getNextAbleResults<'value', VerificationSwagger>(
            (nextLink: string) => getCluTrainedModelService().getVerificationData({ nextLink, projectName, trainedModelName }),
            'value'
        );

        transaction(() => {
            currentModel.setModelVerificationData(modelVerificationData);
        });
    };

    const getCluTrainedModelGuidanceReport = async ({
        trainedModelNames,
        isRefreshEvaluation = false
    }: HandlerParams<{ trainedModelNames: string[]; isRefreshEvaluation?: boolean }>) => {
        const currentProject = rootStore.currentCluProjectStore;
        const trainedModelsStoreHouse = currentProject.trainedModelStoreHouse;

        if (trainedModelsStoreHouse.tracker.isRunning) {
            await when(() => trainedModelsStoreHouse.tracker.isCompleted);
        } else if (!trainedModelsStoreHouse.tracker.isCompleted) {
            await loadCluTrainedModelsHelper(rootStore);
        }

        const modelService = getCluTrainedModelService();
        const modelsToFetch = currentProject.trainedModelStoreHouse.items
            .filter(model => trainedModelNames.includes(model.name))
            .filter(model => (!model.evaluationTracker.isCompleted || isRefreshEvaluation) && !model.modelGuidanceTracker.isRunning);

        transaction(() => modelsToFetch.forEach(m => m.modelGuidanceTracker.setState('running')));

        const promiseResults = await batchPromises(
            modelsToFetch.map(model =>
                modelService.getTrainedModelGuidanceReport({ projectName: currentProject.name, trainedModelName: model.name })
            )
        );

        transaction(() => {
            promiseResults.results.forEach((result, index) => {
                if (result.status === 'success') {
                    const stores = cluModelGuidanceSwaggersToStore(result.success);
                    modelsToFetch[index].setEntityModelGuidanceStore(stores.EntityModelGuidanceStore);
                    modelsToFetch[index].setIntentModelGuidanceStore(stores.intentModelGuidanceStore);
                    modelsToFetch[index].modelGuidanceTracker.setState('completed');
                } else {
                    modelsToFetch[index].setEntityModelGuidanceStore(null);
                    modelsToFetch[index].setIntentModelGuidanceStore(null);
                    modelsToFetch[index].modelGuidanceTracker.setState('failed', <Error>result.error);
                }
            });
        });
    };

    const getCluTrainedModelAllData = async ({
        trainedModelNames,
        isRefreshEvaluation = false
    }: HandlerParams<{ trainedModelNames: string[]; isRefreshEvaluation?: boolean }>) => {
        if (!rootStore.currentCluProjectStore) return;
        await Promise.all([
            getCluTrainedModelEvaluationData({ trainedModelNames, isRefreshEvaluation }),
            getCluTrainedModelGuidanceReport({ trainedModelNames, isRefreshEvaluation })
        ]);
    };

    const deleteCluTrainedModels = async ({
        projectName,
        modelNames,
        generateMultiFailureMessage
    }: HandlerParams<{ projectName: string; modelNames: string[]; generateMultiFailureMessage: (failedCount: number) => string }>) => {
        const { cluProjectStoreHouse } = rootStore;
        const trainedModelsStoreHouse = cluProjectStoreHouse.findById(projectName).trainedModelStoreHouse;
        trainedModelsStoreHouse.tracker.setState('running');

        const { results, failedCount } = await batchPromises(
            modelNames.map(modelName => getCluTrainedModelService().deleteTrainedModel({ projectName, trainedModelName: modelName }))
        );

        const deletedModelsNames = results.filter(res => res.status === 'success').map((_, index) => modelNames[index]);

        if (failedCount > 0) {
            throw new Error(generateMultiFailureMessage(failedCount));
        }

        transaction(() => {
            trainedModelsStoreHouse.setItems(trainedModelsStoreHouse.items.filter(m => deletedModelsNames.every(name => m.name !== name)));
            trainedModelsStoreHouse.tracker.setState('completed');
        });
    };

    const loadCluModelSnapshot = async ({
        projectName,
        trainedModelName
    }: HandlerParams<{ projectName: string; trainedModelName: string }>) => {
        const currentProject = rootStore.currentCluProjectStore;
        const trainedModelStore = currentProject.trainedModelStoreHouse.items.find(trainedModel => trainedModel.name === trainedModelName);

        if (trainedModelStore.hasSnapshot) {
            const [_, projectDataSwagger] = await Promise.all([
                getCluTrainedModelService().loadModelSnapshotAndPoll({
                    projectName,
                    trainedModelName,
                    operationHandler: {
                        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                        retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
                        handleCall: data => {
                            if (data.status === 'failed') {
                                throw new Error(data.errors?.[0]?.message);
                            }
                        }
                    }
                }),
                getCluProjectService().getProjectData({
                    projectName: currentProject.name,
                    trainedModelName
                })
            ]);

            currentProject.updateProject(projectDataSwagger);
        } else {
            throw new Error("Model doesn't have a snapshot. Please retrain the model.");
        }
    };

    const refreshEvaluation = async ({
        projectName,
        trainedModelName
    }: HandlerParams<{ projectName: string; trainedModelName: string }>) => {
        const currentProject = rootStore.currentCluProjectStore;

        let jobId: string;

        try {
            const statusUrl = await getCluTrainedModelService().initiateRefreshEvaluationAndPoll({
                projectName,
                trainedModelName
            });

            jobId = new URL(statusUrl).pathname.split('/').pop();
            currentProject.setEvaluationJobStore(new EvaluationJobStore(jobId, { status: 'inProgress', modelId: trainedModelName }));

            await getCluTrainedModelService().pollRefreshEvaluation({
                statusUrl,
                operationHandler: getEvaluationJobOperationHandler(lifetime, currentProject.evaluationJobStore)
            });

            if (currentProject.evaluationJobStore.status === 'succeeded') {
                await getCluTrainedModelAllData({
                    trainedModelNames: [currentProject.evaluationJobStore.modelId],
                    isRefreshEvaluation: true
                });
            }
        } catch (e) {
            currentProject.evaluationJobStore?.setStatus('failed');
        }
    };

    return {
        getCluTrainedModels,
        getCluTrainedModelAllData,
        getCluTrainedModelEvaluationData,
        getCluTrainedModelVerificationData,
        deleteCluTrainedModels,
        loadCluModelSnapshot,
        refreshEvaluation
    };
};
